import React, { useState } from "react";
import { X, CheckCircle, User, Star } from "lucide-react";
import { toast } from "react-toastify";
import { motion, AnimatePresence } from "framer-motion";
import affiliateService from "../../requests/affiliates/AffiliateService";

const ApproveApplicationModal = ({ application, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);

  const handleApprove = async () => {
    setLoading(true);
    try {
      const payload = {
        id: application.id,
        status: "approved"
      };
      
      const response = await affiliateService.approveApplication(application.id, payload);
      if (response.data?.status_code ==="000") {
        toast.success(response.data?.reason || "Application approved successfully");
        onSuccess();
      } else {
        toast.error(response.data?.reason || "Failed to approve application");
      }
    } catch (error) {
      toast.error("Failed to approve application");
    } finally {
      setLoading(false);
    }
  };

  const affiliate = application.affiliate;
  const deal = application.deal;

  return (
    <AnimatePresence>
      <motion.div 
        className="overflow-y-auto fixed inset-0 z-50"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.3 }}
      >
        {/* Backdrop */}
        <motion.div
          className="fixed inset-0 z-40 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={onClose}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        ></motion.div>

        <div className="flex relative z-50 justify-center items-center px-4 pt-4 pb-20 min-h-screen text-center sm:block sm:p-0">
          {/* Modal */}
          <motion.div 
            className="inline-block overflow-hidden relative z-50 text-left align-bottom bg-white rounded-2xl shadow-xl transition-all transform sm:my-8 sm:align-middle sm:max-w-lg sm:w-full"
            initial={{ opacity: 0, scale: 0.8, y: 50 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.8, y: 50 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
          >
          {/* Header */}
          <div className="px-6 py-4 bg-gradient-to-r from-green-500 to-green-600">
            <div className="flex justify-between items-center">
              <div className="flex items-center">
                <CheckCircle className="mr-3 w-6 h-6 text-white" />
                <h3 className="text-lg font-semibold text-white">
                  Approve Application
                </h3>
              </div>
              <button
                onClick={onClose}
                className="text-white transition-colors hover:text-green-200"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="px-6 py-4 bg-white">
            {/* Affiliate Info */}
            <div className="flex items-center mb-4 space-x-3">
              <div className="flex justify-center items-center w-10 h-10 bg-blue-100 rounded-full">
                <User className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h4 className="font-semibold text-gray-900">
                  {affiliate.first_name} {affiliate.last_name}
                </h4>
                <p className="text-sm text-gray-600">{affiliate.email}</p>
              </div>
            </div>

            {/* Deal Info */}
            <div className="flex items-center mb-4 space-x-3">
              <div className="flex justify-center items-center w-10 h-10 bg-purple-100 rounded-full">
                <Star className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <h4 className="text-sm font-semibold text-gray-900">
                  {deal.title}
                </h4>
                <p className="text-sm text-gray-600">{deal.brand_name}</p>
              </div>
            </div>

            {/* Reach Info */}
            {affiliate.social && affiliate.social.length > 0 && (
              <div className="p-3 mb-4 bg-gray-50 rounded-lg">
                <div className="flex justify-between items-center text-sm">
                  <span className="text-gray-600">Total Reach:</span>
                  <span className="font-semibold text-gray-900">
                    {affiliate.social.reduce((total, social) => total + parseInt(social.followers.replace(/,/g, '')), 0).toLocaleString()} followers
                  </span>
                </div>
              </div>
            )}

            {/* Confirmation */}
            <div className="p-3 bg-green-50 rounded-lg border border-green-200">
              <div className="flex items-start">
                <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 mr-2" />
                <p className="text-sm text-green-800">
                  This affiliate will be approved and can start promoting your deal.
                </p>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end px-6 py-3 space-x-3 bg-gray-50">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white rounded-lg border border-gray-300 transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-gray-500"
            >
              Cancel
            </button>
            <button
              onClick={handleApprove}
              disabled={loading}
              className="flex items-center px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg border border-transparent transition-colors hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <div className="mr-2 w-4 h-4 rounded-full border-b-2 border-white animate-spin"></div>
                  Approving...
                </>
              ) : (
                <>
                  <CheckCircle className="mr-2 w-4 h-4" />
                  Approve
                </>
              )}
            </button>
          </div>
          </motion.div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default ApproveApplicationModal;
